import { createContext, useCallback, useContext, useEffect, useState, type ComponentProps } from "react";
import { createPortal } from "react-dom";
import type { AxiosInstance } from "axios";
import { Alert, Button, Drawer, Modal, Layout as RefineLayout, Space, Spin, Tag } from "@pankod/refine-antd";
import styles from "./OpsAssistant.module.css";

function AssistantIcon() {
  return <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 10 9l-6 2 6 2 2 6 2-6 6-2-6-2-2-6Z" />
    <path d="M19 3v4m-2-2h4M5 16v4m-2-2h4" />
  </svg>;
}

export const assistantPageEvent = "bhs:assistant-page";
type Design = { html: string; css: string };
export type PageEdit = { action: string; target: string; value: string; heading: string };
export type PageBlock = { id: string; tag: string; text: string; parent: string; href: string };
export type RedesignSelection = {
  redesign: true;
  id: string;
  title: string;
  text: string;
  blocks: PageBlock[];
  selectedBlock?: string;
  previewEdits: (edits: PageEdit[]) => Design;
  applyEdits: (edits: PageEdit[]) => Promise<void>;
  apply: (design: Design) => Promise<void>;
};

type Section = { key: string; label: string };
type Layout = { hero: boolean; visual: boolean; sections: Section[] };
type Result =
  | { kind: "pages"; pages: { title: string; slug: string; published: boolean }[]; truncated: boolean }
  | { kind: "homepage"; published: Layout; draft: Layout | null; fingerprint: string }
  | { kind: "message"; message: string };
type Turn = {
  question?: string;
  id: string;
  status: "queued" | "running" | "complete" | "failed" | "proposed" | "applied" | "dismissed" | "expired";
  result?: Result;
  message?: string;
  pageId?: string;
  proposal?: { kind: "rewrite"; selectionId: string; before: string; after: string; expiresAt: number }
    | { kind: "edit"; selectionId: string; title: string; edits: PageEdit[]; expiresAt: number }
    | { kind: "redesign"; selectionId: string; title: string; design: Design; expiresAt: number }
    | { kind: "reorder"; before: Section[]; after: Section[]; scope: string }
    | { kind: "page"; scope: string; design: { title: string; slug: string; sections: { type: string; heading: string; body: string; buttonLabel: string; buttonPath: string }[] } };
};
type History = { turns: Turn[]; modelEnabled: boolean; scope: string };

const pending = (turn: Turn) => turn.status === "queued" || turn.status === "running";

function SectionOrder({ sections }: { sections: Section[] }) {
  return <ol className={styles.order}>{sections.map((section) => <li key={section.key}>{section.label}</li>)}</ol>;
}

function LayoutSummary({ layout }: { layout: Layout }) {
  return <>
    <p>Hero carousel {layout.hero ? "on" : "off"} · Visual section {layout.visual ? "on" : "off"}</p>
    <SectionOrder sections={layout.sections} />
  </>;
}

const Rail = createContext<HTMLDivElement | null>(null);
const desktopQuery = "(min-width: 1150px)";

export function OpsAssistantLayout(props: ComponentProps<typeof RefineLayout>) {
  const [rail, setRail] = useState<HTMLDivElement | null>(null);
  return <Rail.Provider value={rail}>
    <div className={styles.deck}>
      <div className={styles.main}><RefineLayout {...props} /></div>
      <div className={styles.rail} ref={setRail} />
    </div>
  </Rail.Provider>;
}

export function OpsAssistantHeader({ api, apiUrl }: { api: AxiosInstance; apiUrl: string }) {
  const [open, setOpen] = useState(false);
  const [desktop, setDesktop] = useState(() => window.matchMedia(desktopQuery).matches);
  const rail = useContext(Rail);
  useEffect(() => {
    const media = window.matchMedia(desktopQuery);
    const update = () => setDesktop(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const [history, setHistory] = useState<History>();
  const [accessDenied, setAccessDenied] = useState(false);
  const [active, setActive] = useState("");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<Section[]>();
  const [selection, setSelection] = useState<RedesignSelection>();
  const [page, setPage] = useState<{ title: string; capture: () => RedesignSelection | undefined }>();
  useEffect(() => {
    const receivePage = (event: Event) => {
      setPage((event as CustomEvent<typeof page>).detail);
      setSelection(undefined);
      setReference(undefined);
    };
    window.addEventListener(assistantPageEvent, receivePage);
    return () => window.removeEventListener(assistantPageEvent, receivePage);
  }, []);
  const [reference, setReference] = useState<{ file: File }>();
  const [previewWidth, setPreviewWidth] = useState<number>();
  const [editPreview, setEditPreview] = useState<Design>();
  const endpoint = `${apiUrl}/ops-assistant/turns`;

  const load = useCallback(async () => {
    const { data } = await api.get<History>(endpoint);
    setHistory(data);
  }, [api, endpoint]);

  useEffect(() => {
    let mounted = true;
    api.get<History>(endpoint).then(({ data }) => {
      if (mounted) setHistory(data);
    }).catch((failure) => {
      if (mounted && failure.response?.status === 403) setAccessDenied(true);
    });
    return () => { mounted = false; };
  }, [api, endpoint]);

  const turns = history?.turns ?? [];
  const turn = turns.find((item) => item.id === active) ?? turns.find(() => active !== "new");
  const watching = Boolean(turn && pending(turn));
  useEffect(() => {
    if (!open || !watching) return;
    const timer = window.setInterval(() => void load().catch(() => undefined), 1000);
    return () => window.clearInterval(timer);
  }, [load, open, watching]);

  async function send(body: Record<string, unknown>, image?: File) {
    setError("");
    setBusy(true);
    try {
      if (image && body.selection && typeof body.selection === "object") {
        const url = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(image);
        });
        body.selection = { ...body.selection, image: url };
      }
      const { data } = await api.post<Turn>(endpoint, body);
      setHistory((current) => current && { ...current, turns: [data, ...current.turns].slice(0, 12) });
      setActive(data.id);
      setOrder(undefined);
      setQuestion("");
      setReference(undefined);
    } catch (failure) {
      const message = failure.response?.data?.message;
      setError(typeof message === "string" ? message : "Could not start the request. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function decide(action: "approve" | "dismiss") {
    if (!turn) return;
    setError("");
    setBusy(true);
    try {
      if (action === "approve" && (turn.proposal?.kind === "redesign" || turn.proposal?.kind === "edit")) {
        if (!selection?.redesign || turn.proposal.selectionId !== selection.id || turn.proposal.expiresAt <= Date.now()) {
          throw new Error("Open the page and request a fresh redesign.");
        }
        if (turn.proposal.kind === "edit") await selection.applyEdits(turn.proposal.edits);
        else await selection.apply(turn.proposal.design);
      }
      const { data } = await api.post<Turn>(`${endpoint}/${turn.id}/${action}`);
      if (action === "approve") setSelection(undefined);
      setHistory((current) => current && { ...current, turns: current.turns.map((item) => item.id === data.id ? data : item) });
    } catch (failure) {
      const message = failure.response?.data?.message;
      setError(typeof message === "string" ? message : failure.message || "Could not update this request.");
    } finally {
      setBusy(false);
    }
  }

  if (!history) return accessDenied ? (
    <header className={styles.header}>
      <Button className={styles.trigger} icon={<AssistantIcon />} href="/admin-tools"
        aria-label="Ops Assistant access" title="Check your Ops Assistant access in Admin Tools" />
    </header>
  ) : null;
  const result = turn?.result;
  const proposal = turn?.proposal;
  const editorProposal = proposal?.kind === "redesign" || proposal?.kind === "rewrite" || proposal?.kind === "edit";
  const revising = turn?.status === "proposed" && proposal?.kind === "edit" && proposal.selectionId === selection?.id;
  const lock = busy || watching || (turn?.status === "proposed" && !revising && (!editorProposal || proposal.selectionId === selection?.id));
  const redesigning = Boolean(page);
  const move = (index: number, offset: number) => {
    if (!order) return;
    const next = [...order];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    setOrder(next);
  };

  const composer = history.modelEnabled && <form className={styles.ask} onSubmit={(event) => {
          event.preventDefault();
          if (!question.trim() || lock) return;
          const context = revising && selection ? { ...selection, id: crypto.randomUUID() } : page?.capture();
          if (page && !context) {
            setError("The page editor is busy. Close the preview or finish saving and try again.");
            return;
          }
          setSelection(context);
          void send({ question, ...(revising && { revise: { turnId: turn?.id, selectionId: selection?.id } }), ...(context && {
            selection: { id: context.id, text: context.text,
              redesign: true, title: context.title, blocks: context.blocks, selectedBlock: context.selectedBlock },
          }) }, context?.redesign ? reference?.file : undefined);
        }}>
          <div className={styles.messageBox}>
            <textarea rows={2} value={question} maxLength={1000} placeholder={redesigning ? "Describe what to improve on this page" : "Ask a question or describe a page"} aria-label="Ask Ops Assistant"
              onChange={(event) => setQuestion(event.target.value)} disabled={lock}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }} />
            <button className={styles.send} type="submit" aria-label="Send" title="Send" disabled={!question.trim() || lock}>
              <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 19V5m-6 6 6-6 6 6" />
              </svg>
            </button>
          </div>
          {redesigning && <div className={styles.attachments}>
            <label className={styles.attach}>Attach reference image
              <input type="file" accept="image/png,image/jpeg,image/webp" disabled={lock} onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 700 * 1024) {
                  setError("Choose a PNG, JPEG or WebP image up to 700 KB.");
                  return;
                }
                setError("");
                setReference({ file });
              }} />
            </label>
            {reference && <p>{reference.file.name} <Button size="small" disabled={lock} onClick={() => setReference(undefined)}>Remove image</Button></p>}
          </div>}
        </form>;

  const content = (
      <div className={styles.panel}>
        <section className={styles.intro}>
          <Tag color="purple">Pages</Tag>
          <p>{history.scope}</p>
          <p>{page ? `Editing ${page.title}. Describe the changes you want to preview.` : "Describe a page to create, or ask about your drafts."}</p>
        </section>

        {error && <Alert type="error" showIcon message={error} />}
        {turn?.question && <div className={styles.userMessage}><strong>You</strong><div>{turn.question}</div></div>}
        {watching && <div className={styles.working} role="status">
          <Spin size="small" /> {turn?.status === "queued" ? "Queued" : selection?.redesign ? "Designing the page" : "Working"}…
          <Button size="small" onClick={() => decide("dismiss")} disabled={busy}>Cancel</Button>
        </div>}
        {selection && <Alert type="info" message="Redesigning the open page" description={selection.title}
          closable onClose={() => setSelection(undefined)} />}
        {turn && !watching && <section className={styles.card} aria-live="polite">
          {result?.kind === "pages" && <>
            <h2>Unpublished page drafts</h2>
            {result.pages.length ? <ul className={styles.pages}>{result.pages.map((page) => <li key={page.slug}>
              <strong>{page.title}</strong>
              <span>/{page.slug} · {page.published ? "Published with draft" : "Awaiting first publish"}</span>
            </li>)}</ul> : <p>There are no unpublished page drafts.</p>}
            {result.truncated && <p>Showing the 25 most recent drafts.</p>}
          </>}
          {result?.kind === "homepage" && <>
            <h2>Homepage layout</h2>
            <h3>Published</h3>
            <LayoutSummary layout={result.published} />
            {result.draft && <><h3>Saved draft</h3><LayoutSummary layout={result.draft} /></>}
            {!order && <Button onClick={() => setOrder((result.draft ?? result.published).sections)}>Reorder in a draft</Button>}
            {order && <div className={styles.edit}>
              <h3>Proposed section order</h3>
              <ol className={styles.sortable}>{order.map((section, index) => <li key={section.key}>
                <span>{section.label}</span>
                <Space size={4}>
                  <Button size="small" aria-label={`Move ${section.label} up`} disabled={index === 0}
                    onClick={() => move(index, -1)}>↑</Button>
                  <Button size="small" aria-label={`Move ${section.label} down`} disabled={index === order.length - 1}
                    onClick={() => move(index, 1)}>↓</Button>
                </Space>
              </li>)}</ol>
              <Space wrap>
                <Button onClick={() => setOrder(undefined)}>Cancel reorder</Button>
                <Button type="primary" disabled={lock || order.every((section, index) =>
                  section.key === (result.draft ?? result.published).sections[index]?.key)}
                  onClick={() => send({ tool: "reorder_homepage", fingerprint: result.fingerprint, order: order.map(({ key }) => key) })}>
                  Review reorder
                </Button>
              </Space>
            </div>}
          </>}
          {result?.kind === "message" && <p>{result.message}</p>}
          {turn.proposal?.kind === "rewrite" && <>
            <h2>Text rewrite</h2>
            <h3>Current text</h3><p>{turn.proposal.before}</p>
            <h3>Proposed text</h3><p>{turn.proposal.after}</p>
          </>}
          {turn.proposal?.kind === "redesign" && <>
            <h2>Redesign: {turn.proposal.title}</h2>
            <Button onClick={() => setPreviewWidth(1024)}>Preview redesign</Button>
            <Modal title="Proposed redesign" visible={previewWidth !== undefined} onCancel={() => setPreviewWidth(undefined)}
              footer={null} width={1080} destroyOnClose>
              <Space wrap>
                <Button onClick={() => setPreviewWidth(1024)}>Desktop</Button>
                <Button onClick={() => setPreviewWidth(390)}>Mobile</Button>
              </Space>
              <iframe title="Proposed page layout" sandbox="" style={{ display: "block", width: previewWidth, maxWidth: "100%", height: "60vh", margin: "12px auto" }}
                srcDoc={`<!doctype html><meta name="viewport" content="width=device-width, initial-scale=1"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><style>body{margin:0;font-family:system-ui,sans-serif}a{pointer-events:none;color:inherit}${turn.proposal.design.css}</style>${turn.proposal.design.html}`} />
            </Modal>
          </>}
          {turn.proposal?.kind === "edit" && <>
            <h2>Proposed edits to {turn.proposal.title}</h2>
            <ul>{turn.proposal.edits.map((edit, index) => <li key={index}>{edit.action.replace("_", " ")} · {edit.heading || edit.value || edit.target}</li>)}</ul>
            <Button disabled={turn.proposal.selectionId !== selection?.id} onClick={() => {
              if (!selection || turn.proposal?.kind !== "edit") return;
              try {
                setEditPreview(selection.previewEdits(turn.proposal.edits));
                setPreviewWidth(1024);
              } catch (failure) { setError(failure.message); }
            }}>Preview edits</Button>
            <Modal title="Proposed edits" visible={previewWidth !== undefined && Boolean(editPreview)} onCancel={() => { setEditPreview(undefined); setPreviewWidth(undefined); }}
              footer={null} width={1080} destroyOnClose>
              <Space><Button onClick={() => setPreviewWidth(1024)}>Desktop</Button><Button onClick={() => setPreviewWidth(390)}>Mobile</Button></Space>
              {editPreview && <iframe title="Proposed page edits" sandbox="" style={{ display: "block", width: previewWidth, maxWidth: "100%", height: "60vh" }}
                srcDoc={`<!doctype html><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'"><style>${editPreview.css}</style>${editPreview.html}`} />}
            </Modal>
          </>}
          {turn.proposal?.kind === "reorder" && <>
            <h2>Homepage reorder</h2>
            <p>{turn.proposal.scope}</p>
            <div className={styles.diff}>
              <div><h3>Current order</h3><SectionOrder sections={turn.proposal.before} /></div>
              <div><h3>Proposed order</h3><SectionOrder sections={turn.proposal.after} /></div>
            </div>
          </>}
          {turn.proposal?.kind === "page" && <>
            <h2>{turn.proposal.design.title}</h2>
            <p>/{turn.proposal.design.slug} · {turn.proposal.scope}</p>
            <ol className={styles.sortable}>
              {turn.proposal.design.sections.map((section, index) => <li key={index}>
                <div>
                  <Tag>{section.type.replace(/_/g, " ")}</Tag>
                  <h3>{section.heading}</h3>
                  <p>{section.body}</p>
                  {section.buttonLabel && <p>{section.buttonLabel} → {section.buttonPath}</p>}
                </div>
              </li>)}
            </ol>
          </>}
          {turn.status === "proposed" && <>
            <p>{turn.proposal?.kind === "edit" ? "Approve to save the edits, or send a follow-up to revise this proposal." : turn.proposal?.kind === "redesign" ? "Approval replaces the entire canvas, including existing blocks, and saves it as a draft." : turn.proposal?.kind === "rewrite" ? "Approval saves the selected text to the open page draft." : turn.proposal?.kind === "page" ? "Approval creates an editable draft in Pages."
              : "Approval saves the new order as a draft. Publish it from the Pages editor when ready."}</p>
            <Space wrap>
              <Button type="primary" onClick={() => decide("approve")} loading={busy}
                disabled={editorProposal && proposal.selectionId !== selection?.id}>{turn.proposal?.kind === "page" ? "Approve and create page draft" : "Approve and save draft"}</Button>
              <Button onClick={() => decide("dismiss")} disabled={busy}>Dismiss</Button>
            </Space>
          </>}
          {turn.status === "applied" && <Alert type="success" showIcon message={editorProposal ? "Page draft updated" : turn.pageId ? "Page draft created" : "Homepage draft saved"}
            description={!editorProposal && <a href={turn.pageId ? `/campaign-editor?page=${turn.pageId}` : "/campaign-editor"}>{turn.pageId ? "Open draft in GrapesJS" : "Open Pages to preview the draft"}</a>} />}
          {turn.status === "failed" && <Alert type="error" showIcon message={turn.message} />}
          {turn.status === "expired" && <p>This proposal expired. Read the homepage layout to start again.</p>}
          {turn.status === "dismissed" && <p>Request dismissed.</p>}
        </section>}
        {history.turns.length > 1 && <details className={styles.activity}>
          <summary>Recent activity</summary>
          <ul>{history.turns.map((item) => <li key={item.id}>
            <Button type="link" disabled={Boolean(lock)} onClick={() => { setActive(item.id); setOrder(undefined); setError(""); }}>
              {item.proposal ? item.proposal.kind === "page" ? "Page design" : item.proposal.kind === "redesign" ? "Page redesign" : item.proposal.kind === "rewrite" ? "Text rewrite" : "Homepage reorder" : item.result?.kind === "pages" ? "Page drafts" : item.result?.kind === "homepage" ? "Homepage layout" : "Request"}
              {" · "}{item.status}
            </Button>
          </li>)}</ul>
        </details>}
      </div>
  );

  return <>
    <header className={styles.header}>
      <Button className={styles.trigger} icon={<AssistantIcon />} aria-label="Ops Assistant" title="Ops Assistant" aria-expanded={open} onClick={() => {
        setOpen((current) => !current);
        if (!open) void load().catch(() => setError("Could not refresh assistant activity."));
      }} />
    </header>
    {desktop && rail ? open && createPortal(
      <aside aria-label="Ops Assistant" className={styles.desktopPanel}>
        <div className={styles.railHeading}><strong className={styles.railTitle}><AssistantIcon /> Ops Assistant</strong><Button type="text" onClick={() => setOpen(false)}>Close</Button></div>
        <div className={styles.transcript}>{content}</div>
        {composer}
      </aside>, rail
    ) : <Drawer title="Ops Assistant" placement="right" width="min(100vw, 480px)" visible={open}
      onClose={() => setOpen(false)} bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}>
      <div className={styles.transcript}>{content}</div>
      {composer}
    </Drawer>}
  </>;
}
